import React, { useMemo, useRef, useState, useCallback, useEffect, useContext } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, GridReadyEvent, RowClickedEvent, CellClassParams, RowClassParams, CellClickedEvent, ITooltipParams, CellContextMenuEvent } from 'ag-grid-community';
import ContextMenu, { MenuOption } from '../common/ContextMenu';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { IDocument } from '../../types/my-types';
import { useNavigate, useParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { showMarkSpreadsheetAtom } from '../../atoms/mark-atoms';
import {
  markValidationByDocumentAtom,
  markShowValidationsAtom,
  IMarkValidationByDocument,
  IMarkValidationErrorDetails,
} from '../../atoms/mark-validation-atoms';
import { useTrainingSetSpreadsheet } from './hooks/useTrainingSetSpreadsheet';
import { useMarkValidation } from '../hooks/useMarkValidation';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import { MarkDispatchContext, MarkStateContext } from '../../context/MarkContext';
import { AppContext } from '../../App';
import {
  getCellStyles,
  getHighestPriorityValidationError,
  getValidationMessages,
  isRequiredAndValueEmpty,
} from '../../utils/validationUtils';
import { TValidationType, IValidationErrorByExtractionDetails } from '../../types/validation-types';
import './SpreadsheetMark.styles.css';

interface SpreadsheetMarkProps {
  docs: IDocument[];
  trainingSetId: string;
}

// Helper to get validation errors for a specific field in a document
const getMarkValidationErrorsForField = (
  validationByDocument: IMarkValidationByDocument[],
  documentId: string,
  fieldLabel: string
): IMarkValidationErrorDetails[] => {
  const docValidation = validationByDocument.find((v) => v.documentId === documentId);
  if (!docValidation) return [];
  return docValidation.validationErrorDetails.filter((e) => e.field === fieldLabel);
};

// Helper to check if field has ignored errors
const fieldHasIgnoredErrors = (errors: IMarkValidationErrorDetails[]): boolean => {
  return errors.some((e) => e.dismissalReason !== null && e.dismissalReason !== undefined);
};

// Type for field metadata
interface IMarkFieldMeta {
  validationStatus: 'required' | 'error' | null;
  tooltip: string;
  ignored: boolean;
  dismissalReason: string | null;
}

const SpreadsheetMark: React.FC<SpreadsheetMarkProps> = ({ docs, trainingSetId }) => {
  const navigate = useNavigate();
  const { dpid, did } = useParams<{ dpid: string; did: string }>();
  const showMarkSpreadsheet = useAtomValue(showMarkSpreadsheetAtom);
  const showValidations = useAtomValue(markShowValidationsAtom);
  const validationByDocument = useAtomValue(markValidationByDocumentAtom);
  const { accessibility } = useContext(AppContext);
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const isUserClickNavigation = useRef(false);
  const ROW_HEIGHT = 28;
  const HEADER_HEIGHT = 32;
  const MAX_GRID_HEIGHT = 300;
  const MIN_GRID_HEIGHT = 50;

  const { activeField, spreadsheetFocusedCell } = React.useContext(MarkStateContext);
  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);
  const dragListenerRef = useRef<((e: MouseEvent) => void) | null>(null);

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; visible: boolean; cellValue: string }>({
    x: 0,
    y: 0,
    visible: false,
    cellValue: '',
  });

  const { rows, columns, isLoading } = useTrainingSetSpreadsheet(docs, trainingSetId);

  // Get document IDs for validation - if grid is shown, validate all docs; otherwise just current
  const documentIdsForValidation = useMemo(() => {
    if (showMarkSpreadsheet) {
      return docs.map((d) => d.id);
    }
    return did ? [did] : [];
  }, [showMarkSpreadsheet, docs, did]);

  // Fetch validation data
  const { isLoading: isValidationLoading } = useMarkValidation({
    trainingSetId,
    documentIds: documentIdsForValidation,
    enabled: showMarkSpreadsheet || !!did,
  });

  // Merge rows with docs to ensure we have all docs and their metadata, including validation fieldMeta
  const rowData = useMemo(() => {
    const rowsMap = new Map(rows.map(r => [r.id, r]));
    return docs.map(doc => {
      const rowFields: Record<string, string> = rowsMap.get(doc.id) || {};

      // Build fieldMeta for each field with validation info
      const fieldMeta: Record<string, IMarkFieldMeta> = {};

      columns.forEach(col => {
        const fieldLabel = col.headerName;
        const fieldKey = col.field as string;
        if (!fieldLabel || !fieldKey) return;

        const fieldValue = rowFields[fieldKey] || '';
        const validationErrors = getMarkValidationErrorsForField(
          validationByDocument,
          doc.id,
          fieldLabel
        );

        // Convert to the format expected by validation utils
        const errorsAsExtractionDetails: IValidationErrorByExtractionDetails[] = validationErrors.map(e => ({
          id: e.id,
          field: e.field,
          error: e.error,
          errorDetail: e.errorDetail,
          type: e.type,
          dismissalReason: e.dismissalReason,
        }));

        const highestPriorityError = getHighestPriorityValidationError(errorsAsExtractionDetails);
        const tooltipMessages = getValidationMessages(errorsAsExtractionDetails, fieldValue);
        const hasIgnoredErrors = fieldHasIgnoredErrors(validationErrors);

        let validationStatus: 'required' | 'error' | null = null;
        if (highestPriorityError?.type) {
          validationStatus = highestPriorityError.type === 'required' ? 'required' : 'error';
        }

        fieldMeta[fieldLabel] = {
          validationStatus,
          tooltip: tooltipMessages.join(', ') || '',
          ignored: hasIgnoredErrors,
          dismissalReason: validationErrors.find(e => e.dismissalReason)?.dismissalReason ?? null,
        };
      });

      return {
        ...doc,
        ...rowFields,
        fieldMeta,
      };
    });
  }, [docs, rows, columns, validationByDocument]);

  // Calculate initial height based on row count
  const calculatedHeight = useMemo(() => {
    const contentHeight = HEADER_HEIGHT + (docs.length * ROW_HEIGHT);
    return Math.min(Math.max(contentHeight, MIN_GRID_HEIGHT), MAX_GRID_HEIGHT);
  }, [docs.length]);

  const [size, setSize] = useState<{ y: number }>({ y: calculatedHeight });

  // Update size when docs change
  useEffect(() => {
    setSize({ y: calculatedHeight });
  }, [calculatedHeight]);

  // Column Definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    const staticCols: ColDef[] = [
      { field: 'id', headerName: 'Document ID', flex: 1, minWidth: 200, hide: true },
      {
        headerName: '#',
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        valueGetter: (params) => params.node?.rowIndex != null ? params.node.rowIndex + 1 : '',
        sortable: false,
        suppressSizeToFit: true,
      },
    ];

    const dynamicCols = columns.map(col => ({
      ...col,
      cellStyle: (params: CellClassParams) => {
        const fieldLabel = params.colDef.headerName as string | undefined;
        if (!fieldLabel) return {};
        const fieldMeta = params.data?.fieldMeta?.[fieldLabel] as IMarkFieldMeta | undefined;

        const isHighlighted =
          spreadsheetFocusedCell &&
          (params.data.id === spreadsheetFocusedCell.rowId || (!spreadsheetFocusedCell.rowId && params.data.id === did)) &&
          fieldLabel === spreadsheetFocusedCell.colId;

        return getCellStyles({
          accessibility,
          validationStatus: showValidations ? fieldMeta?.validationStatus || null : null,
          isIgnored: showValidations && !!fieldMeta?.ignored,
          isHighlighted: !!isHighlighted,
        });
      },
      tooltipValueGetter: (params: ITooltipParams): string | null => {
        if (!showValidations) return null;
        const fieldLabel = params.colDef?.headerName as string | undefined;
        if (!fieldLabel) return null;
        const fieldMeta = params.data?.fieldMeta?.[fieldLabel] as IMarkFieldMeta | undefined;
        if (!fieldMeta?.validationStatus) return null;
        if (fieldMeta.ignored) {
          const reason = fieldMeta.dismissalReason ? ` (Reason: ${fieldMeta.dismissalReason})` : '';
          return `Error Ignored - ${fieldMeta.tooltip}${reason}`.trim();
        }
        return fieldMeta.tooltip || null;
      },
    } as ColDef));

    return [...staticCols, ...dynamicCols];
  }, [columns, spreadsheetFocusedCell, did, showValidations, accessibility]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
  }), []);

  const rowClassRules = useMemo(() => ({
    'current-document-row': (params: RowClassParams) => params.data.id === did,
  }), [did]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
  }, []);

  // Scroll to current document (only on initial load or external navigation, not user clicks)
  useEffect(() => {
    if (gridApi && did) {
      if (isUserClickNavigation.current) {
        isUserClickNavigation.current = false;
        return;
      }
      gridApi.forEachNode((node) => {
        if (node.data.id === did) {
          gridApi.ensureNodeVisible(node);
        }
      });
    }
  }, [gridApi, did]);

  const onRowClicked = useCallback((event: RowClickedEvent) => {
    // Handle navigation
    const docId = event.data.id;
    if (docId && dpid && trainingSetId) {
      isUserClickNavigation.current = true;
      navigate(`/data-pools/${dpid}/train/${trainingSetId}/mark/${docId}`);
    }
  }, [navigate, dpid, trainingSetId]);

  const onCellClicked = useCallback((event: CellClickedEvent) => {
    // Handle field selection
    const fieldLabel = event.colDef.headerName;
    const rowId = event.data.id;
    if (fieldLabel && fieldLabel !== 'Document ID' && fieldLabel !== '#') {
      dispatch({
        type: 'SET_SPREADSHEET_FOCUSED_CELL',
        payload: {
          rowId,
          colId: fieldLabel,
          pageIndex: 0,
          fieldListFocused: true
        }
      });
    }
  }, [dispatch]);

  // Context menu handlers
  const onCellContextMenu = useCallback((event: CellContextMenuEvent) => {
    event.event?.preventDefault();
    const mouseEvent = event.event as MouseEvent;
    setContextMenu({
      x: mouseEvent.clientX,
      y: mouseEvent.clientY,
      visible: true,
      cellValue: event.value ?? '',
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, visible: false }));
  }, []);

  const contextMenuOptions: MenuOption[] = useMemo(() => [
    {
      label: 'Copy',
      onClick: () => {
        navigator.clipboard.writeText(contextMenu.cellValue);
      },
      dataCy: 'ctx-menu-item-copy',
    },
  ], [contextMenu.cellValue]);

  const dragHandler = (mouseDownEvent: React.MouseEvent) => {
    mouseDownEvent.preventDefault();
    const startSize = size;
    const startPosition = { y: mouseDownEvent.pageY };
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'row-resize';

    const onMouseMove = (mouseMoveEvent: MouseEvent) => {
      const raw = startSize.y - startPosition.y + mouseMoveEvent.pageY;
      setSize({ y: Math.min(MAX_GRID_HEIGHT, Math.max(MIN_GRID_HEIGHT, raw)) });
    };

    dragListenerRef.current = onMouseMove;

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      dragListenerRef.current = null;
      document.body.style.cursor = prevCursor;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp, { once: true });
  };

  // Cleanup drag listener on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (dragListenerRef.current) {
        window.removeEventListener('mousemove', dragListenerRef.current);
        dragListenerRef.current = null;
      }
    };
  }, []);

  if (!showMarkSpreadsheet) {
    return null;
  }

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <>
      <div
        className="ag-theme-balham ag-glynt"
        style={{ height: `${size.y}px`, width: '100%', overflowX: 'auto' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowClassRules={rowClassRules}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          onCellClicked={onCellClicked}
          onCellContextMenu={onCellContextMenu}
          suppressContextMenu={true}
          suppressCellFocus={true}
          suppressScrollOnNewData={true}
          tooltipShowDelay={200}
        />
      </div>
      <hr
        onMouseDown={dragHandler}
        className="dragGrid"
        style={{ cursor: 'row-resize', userSelect: 'none' }}
        aria-label="Resize table height"
      />
      {contextMenu.visible && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={contextMenuOptions}
          onClose={closeContextMenu}
          isIgnoreErrorActive={false}
        />
      )}
    </>
  );
};

export default SpreadsheetMark;
